import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  doc,
  arrayRemove,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../db/firebase";
import { useAuth } from "../../context/AuthContext";
import {
  HiOutlineUserPlus,
  HiOutlineCheck,
  HiOutlineTrash,
  HiOutlinePencil,
  HiOutlineUsers,
  HiOutlineClock,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import InviteUser from "./InviteUser";
import EditUserModal from "./EditUserModal";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../components/Loading";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors";

const glassCard =
  "rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden";

type OrgUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  approved?: boolean;
  profilePicture?: string;
  [key: string]: any;
};

const UserAvatar: React.FC<{ user: OrgUser; className?: string }> = ({
  user,
  className = "w-10 h-10",
}) => {
  const [imgError, setImgError] = useState(false);
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");

  if (user.profilePicture && !imgError) {
    return (
      <img
        src={user.profilePicture}
        alt=""
        onError={() => setImgError(true)}
        className={`${className} rounded-full object-cover shrink-0 ring-1 ring-gray-200/80 dark:ring-gray-600`}
      />
    );
  }

  return (
    <span
      className={`${className} rounded-full shrink-0 flex items-center justify-center text-xs font-semibold bg-[#5932EA]/12 text-[#5932EA] ring-1 ring-[#5932EA]/15`}
      aria-hidden
    >
      {initials || "?"}
    </span>
  );
};

const Users: React.FC = () => {
  const { user, organizationId } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<OrgUser[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<OrgUser[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<OrgUser | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchUsers = async () => {
      if (user && organizationId) {
        setLoading(true);
        try {
          const usersCollection = collection(db, "users");
          const usersQuery = query(
            usersCollection,
            where("organizationId", "==", organizationId),
          );
          const usersSnapshot = await getDocs(usersQuery);

          const usersList: OrgUser[] = usersSnapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          setPendingUsers(usersList.filter((u) => u.approved === false));
          setApprovedUsers(usersList.filter((u) => u.approved === true));
        } catch {
          setError(t("errorFetchingUsers"));
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUsers();
  }, [user, organizationId, t]);

  const matchesSearch = useCallback(
    (u: OrgUser) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const email = (u.email || "").toLowerCase();
      const role = (u.role || "").toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    },
    [search],
  );

  const filteredPending = useMemo(
    () => pendingUsers.filter(matchesSearch),
    [pendingUsers, matchesSearch],
  );
  const filteredApproved = useMemo(
    () => approvedUsers.filter(matchesSearch),
    [approvedUsers, matchesSearch],
  );

  const roleLabel = (role?: string) => {
    const r = (role || "").toLowerCase();
    if (r === "admin" || r === "organization") return t("admin");
    if (r === "member") return t("member");
    return role || "—";
  };

  const approveUser = async (userId: string, userEmail: string) => {
    const userToApprove = pendingUsers.find((u) => u.id === userId);
    if (!userToApprove) return;
    setApprovingId(userId);
    setError("");
    try {
      await updateDoc(doc(db, "users", userId), {
        approved: true,
      });
      setPendingUsers((prev) => prev.filter((u) => u.id !== userId));
      setApprovedUsers((prev) => [
        ...prev,
        { ...userToApprove, approved: true },
      ]);

      const response = await fetch("/.netlify/functions/sendAppovalEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: userEmail }),
      });

      if (!response.ok) {
        throw new Error("Failed to send approval email.");
      }
    } catch (err: any) {
      setError(t("errorApprovingUser") + (err?.message || ""));
    } finally {
      setApprovingId(null);
    }
  };

  const confirmDeleteUser = (u: OrgUser) => {
    setUserToDelete(u);
    setIsDeleteModalOpen(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;

    const userId = userToDelete.id;
    setError("");

    // Best-effort: remove from org ministries/events so stale refs don't block delete.
    if (organizationId) {
      try {
        const batch = writeBatch(db);
        let ops = 0;

        const stripFromDoc = (
          ref: ReturnType<typeof doc>,
          data: Record<string, any>,
        ) => {
          const updates: Record<string, ReturnType<typeof arrayRemove>> = {};
          if (Array.isArray(data.leaders) && data.leaders.includes(userId)) {
            updates.leaders = arrayRemove(userId);
          }
          if (Array.isArray(data.members) && data.members.includes(userId)) {
            updates.members = arrayRemove(userId);
          }
          if (Object.keys(updates).length > 0) {
            batch.update(ref, updates);
            ops += 1;
          }
        };

        const [ministriesSnap, eventsSnap, groupsSnap] = await Promise.all([
          getDocs(
            query(
              collection(db, "ministries"),
              where("organizationId", "==", organizationId),
            ),
          ),
          getDocs(
            query(
              collection(db, "events"),
              where("organizationId", "==", organizationId),
            ),
          ),
          getDocs(
            query(
              collection(db, "groups"),
              where("organizationId", "==", organizationId),
            ),
          ).catch(() => null),
        ]);

        ministriesSnap.docs.forEach((d) => stripFromDoc(d.ref, d.data()));
        eventsSnap.docs.forEach((d) => stripFromDoc(d.ref, d.data()));
        groupsSnap?.docs.forEach((d) => stripFromDoc(d.ref, d.data()));

        if (ops > 0) await batch.commit();
      } catch (cleanupErr) {
        console.warn("User membership cleanup failed; deleting user anyway", cleanupErr);
      }
    }

    try {
      await deleteDoc(doc(db, "users", userId));
      setPendingUsers((prev) => prev.filter((u) => u.id !== userId));
      setApprovedUsers((prev) => prev.filter((u) => u.id !== userId));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      console.error("Error deleting user:", err);
      setError(
        (t("errorDeletingUser") || "Could not delete user. ") +
          (err?.message || ""),
      );
      throw err;
    }
  };

  const handleEditUser = (u: OrgUser) => {
    setSelectedUser(u);
    setIsEditModalOpen(true);
  };

  const handleEditUserSubmit = async (updatedUser: OrgUser) => {
    try {
      const { id, firstName, lastName, email, role } = updatedUser;
      await updateDoc(doc(db, "users", id), {
        firstName,
        lastName,
        email,
        role,
      });
      setApprovedUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, firstName, lastName, email, role } : u,
        ),
      );
      setPendingUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, firstName, lastName, email, role } : u,
        ),
      );
      setIsEditModalOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(t("errorApprovingUser") + (err?.message || ""));
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" message={t("loading")} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4 sm:space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#5932EA]/10 text-[#5932EA] shrink-0">
            <HiOutlineUsers className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight truncate">
              {t("users")}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
              {approvedUsers.length} {t("approvedUsers")}
              {pendingUsers.length > 0
                ? ` · ${pendingUsers.length} ${t("pendingApproval")}`
                : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsInviteModalOpen(true)}
          className={`${glassBtn} shrink-0 !px-3 sm:!px-4 touch-manipulation`}
        >
          <HiOutlineUserPlus className="w-5 h-5" />
          <span className="hidden sm:inline">{t("inviteUser")}</span>
        </button>
      </div>

      <div className="relative">
        <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("searchMembers")}
          className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] text-base sm:text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-manipulation"
            aria-label={t("clear")}
          >
            <HiOutlineXMark className="w-4 h-4" />
          </button>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl border border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Pending */}
      <section className={glassCard}>
        <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-amber-500" />
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            {t("pendingApproval")}
          </h2>
          {pendingUsers.length > 0 && (
            <span className="ml-auto sm:ml-1 px-2 py-0.5 rounded-lg text-xs font-medium bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 tabular-nums">
              {filteredPending.length}
              {search ? ` / ${pendingUsers.length}` : ""}
            </span>
          )}
        </div>
        {filteredPending.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500 dark:text-gray-400">
            <HiOutlineClock className="w-10 h-10 mb-2 opacity-40" />
            <p className="text-sm font-medium">
              {search.trim() ? t("noSearchResults") : t("noPendingUsers")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
            {filteredPending.map((u) => (
              <li
                key={u.id}
                className="flex items-center gap-2.5 sm:gap-3 px-3.5 sm:px-4 py-3 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors"
              >
                <UserAvatar user={u} className="w-10 h-10" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {u.firstName} {u.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {u.email}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => approveUser(u.id, u.email || "")}
                    disabled={approvingId === u.id}
                    className="inline-flex items-center justify-center gap-1 p-2.5 sm:px-3 sm:py-2 rounded-xl text-sm font-medium border border-emerald-300/60 dark:border-emerald-700/50 bg-emerald-50/80 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100/80 disabled:opacity-60 transition-colors touch-manipulation"
                    title={t("approve")}
                    aria-label={t("approve")}
                  >
                    {approvingId === u.id ? (
                      <span className="w-4 h-4 border-2 border-emerald-400/40 border-t-emerald-600 rounded-full animate-spin" />
                    ) : (
                      <HiOutlineCheck className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">{t("approve")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => confirmDeleteUser(u)}
                    className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors touch-manipulation"
                    title={t("delete")}
                    aria-label={t("delete")}
                  >
                    <HiOutlineTrash className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Approved */}
      <section className={glassCard}>
        <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100/80 dark:border-gray-700/80 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-[#5932EA]" />
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            {t("approvedUsers")}
          </h2>
          <span className="ml-auto sm:ml-1 px-2 py-0.5 rounded-lg text-xs font-medium bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] tabular-nums">
            {filteredApproved.length}
            {search ? ` / ${approvedUsers.length}` : ""}
          </span>
        </div>
        {filteredApproved.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500 dark:text-gray-400">
            <HiOutlineUsers className="w-10 h-10 mb-2 opacity-40" />
            <p className="text-sm font-medium">
              {search.trim() ? t("noSearchResults") : t("noApprovedUsers")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
            {filteredApproved.map((u) => (
              <li
                key={u.id}
                className="flex items-center gap-2.5 sm:gap-3 px-3.5 sm:px-4 py-3 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors"
              >
                <UserAvatar user={u} className="w-10 h-10" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {u.firstName} {u.lastName}
                    </p>
                    <span
                      className={`shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${
                        (u.role || "").toLowerCase() === "admin" ||
                        (u.role || "").toLowerCase() === "organization"
                          ? "bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd]"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      <HiOutlineShieldCheck className="w-3 h-3 hidden sm:inline" />
                      {roleLabel(u.role)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {u.email}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEditUser(u)}
                    className="p-2.5 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors touch-manipulation"
                    title={t("edit")}
                    aria-label={t("edit")}
                  >
                    <HiOutlinePencil className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => confirmDeleteUser(u)}
                    className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors touch-manipulation"
                    title={t("delete")}
                    aria-label={t("delete")}
                  >
                    <HiOutlineTrash className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <InviteUser
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      {isEditModalOpen && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
          }}
          onSubmit={handleEditUserSubmit}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={deleteUser}
          userName={
            userToDelete
              ? `${userToDelete.firstName || ""} ${userToDelete.lastName || ""}`.trim()
              : ""
          }
        />
      )}
    </div>
  );
};

export default Users;
